import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Typography, IconButton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import RunDetailsTable from "./RunDetailsTable/RunDetailsTable";
import { getRunDetailsBatchJobs } from "../../API/runDetailsBatchJobApi";
import { getRunDetailsHistory } from "../../API/runDetailsHistoryApi";
import classes from "./LandedCostResults.module.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const VIEW = {
  RUN_DATA: "run-data",
  ITEM_WORKFLOW: "item-workflow",
};

const resolveBatchIdValue = (row) =>
  row?.batchId ?? row?.batch_id ?? row?.batchid ?? row?.id ?? "";

const LandedCostResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get("view");
  const urlBatchId = searchParams.get("batchId");
  const normalizedUrlView = useMemo(
    () => (urlView === VIEW.ITEM_WORKFLOW ? VIEW.ITEM_WORKFLOW : VIEW.RUN_DATA),
    [urlView]
  );
  const normalizedUrlBatchId = useMemo(() => {
    const value = urlBatchId ?? "";
    return value?.length ? value : null;
  }, [urlBatchId]);
  const [activeView, setActiveView] = useState(normalizedUrlView);
  const [runSummaries, setRunSummaries] = useState([]);
  const [runSummariesLoading, setRunSummariesLoading] = useState(false);
  const [runDetails, setRunDetails] = useState([]);
  const [runDetailsLoading, setRunDetailsLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selectedBatchIsNew, setSelectedBatchIsNew] = useState(false);
  const [summarySortModel, setSummarySortModel] = useState([]);
  const [summaryFilterModel, setSummaryFilterModel] = useState({ items: [] });
  const [summaryPage, setSummaryPage] = useState(0);

  useEffect(() => {
    setActiveView(normalizedUrlView);
    if (normalizedUrlView !== VIEW.ITEM_WORKFLOW) {
      setSelectedBatchId(null);
      setSelectedBatchIsNew(false);
    }
  }, [normalizedUrlView]);

  useEffect(() => {
    if (normalizedUrlBatchId) {
      setSelectedBatchId(normalizedUrlBatchId);
    }
  }, [normalizedUrlBatchId]);

  useEffect(() => {
    if (activeView !== VIEW.ITEM_WORKFLOW) {
      return undefined;
    }

    const handleBackspace = (event) => {
      const target = event?.target;
      const tagName = (target?.tagName || "").toUpperCase();
      const isEditable =
        tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable;

      if (event.key === "Backspace" && !isEditable) {
        event.preventDefault();
        setActiveView(VIEW.RUN_DATA);
        setSelectedBatchId(null);
        setSearchParams({}, { replace: true });
      }
    };

    window.addEventListener("keydown", handleBackspace);
    return () => window.removeEventListener("keydown", handleBackspace);
  }, [activeView, setSearchParams]);

  useEffect(() => {
    const fetchRunSummaries = async () => {
      setRunSummariesLoading(true);
      try {
        const data = await getRunDetailsBatchJobs();
        const normalizedData = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.content)
          ? data.content
          : [];
        setRunSummaries(normalizedData);
      } finally {
        setRunSummariesLoading(false);
      }
    };

    fetchRunSummaries();
  }, []);

  useEffect(() => {
    if (!selectedBatchId) {
      setSelectedBatchIsNew(false);
      return;
    }

    const isNew = runSummaries.some(
      (row) =>
        String(resolveBatchIdValue(row)) === String(selectedBatchId) &&
        String(row?.skuStatus || "").toLowerCase() === "new"
    );
    setSelectedBatchIsNew(isNew);
  }, [runSummaries, selectedBatchId]);

  const fetchRunDetails = useCallback(async () => {
    if (!selectedBatchId) {
      setRunDetails([]);
      setSelectedBatchIsNew(false);
      return;
    }

    setRunDetailsLoading(true);
    try {
      const data = await getRunDetailsHistory(selectedBatchId);
      const normalizedData = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.content)
        ? data.content
        : [];
      setRunDetails(normalizedData);
    } finally {
      setRunDetailsLoading(false);
    }
  }, [selectedBatchId]);

  useEffect(() => {
    fetchRunDetails();
  }, [fetchRunDetails]);

  const deriveIsNewFromSummaries = (batchId) => {
    if (!batchId || !Array.isArray(runSummaries)) return false;
    const match = runSummaries.find((row) => String(resolveBatchIdValue(row)) === String(batchId));
    return String(match?.skuStatus || "").toLowerCase() === "new";
  };

  const handleViewChange = (view, batchId = selectedBatchId, batchRow = null) => {
    const nextBatchId = view === VIEW.ITEM_WORKFLOW ? batchId : null;
    const nextIsNew =
      view === VIEW.ITEM_WORKFLOW
        ? String(batchRow?.skuStatus || "").toLowerCase() === "new" ||
          deriveIsNewFromSummaries(batchId)
        : false;
    if (view !== activeView) {
      setActiveView(view);
    }
    setSelectedBatchId(nextBatchId);
    setSelectedBatchIsNew(nextIsNew);
    const nextParams =
      view === VIEW.RUN_DATA
        ? {}
        : {
            view,
            ...(nextBatchId ? { batchId: nextBatchId } : {}),
          };
    setSearchParams(nextParams, { replace: false });
  };

  const renderRunDataSection = () => (
    <section className={classes.section}>
      <header className={classes.sectionHeader}>
        <Typography variant="h6" component="h2" className={classes.sectionTitle}>
          Run Data
        </Typography>
      </header>
      <RunDetailsTable
        runDetails={runSummaries}
        variant="summary"
        showUploadButton={false}
        onUploadClick={() => {}}
        showActions={false}
        loading={runSummariesLoading}
        sortModel={summarySortModel}
        onSortModelChange={setSummarySortModel}
        filterModel={summaryFilterModel}
        onFilterModelChange={setSummaryFilterModel}
        page={summaryPage}
        onPageChange={setSummaryPage}
        onBatchNavigate={(batchId, batchRow) => {
          setSelectedBatchId(batchId);
          handleViewChange(VIEW.ITEM_WORKFLOW, batchId, batchRow);
        }}
      />
    </section>
  );

  const renderItemWorkflowSection = () => (
    <section className={classes.section}>
      <header className={classes.sectionHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconButton
            size="small"
            aria-label="Back to run data"
            onClick={() => handleViewChange(VIEW.RUN_DATA)}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h2" className={classes.sectionTitle}>
            Item Workflow{selectedBatchId ? ` for Batch ${selectedBatchId}` : ""}
          </Typography>
        </div>
      </header>
      <RunDetailsTable
        runDetails={runDetails}
        variant="detail"
        showUploadButton={false}
        showActions={selectedBatchIsNew}
        loading={runDetailsLoading}
        selectedBatchId={selectedBatchId}
        onRefreshData={fetchRunDetails}
        onBatchNavigate={(batchId) => handleViewChange(VIEW.ITEM_WORKFLOW, batchId)}
      />
    </section>
  );

  return (
    <section className={classes.page}>
      <header className={classes.header}>
        <div>
          <h1 className={classes.title}>Landed Cost Results</h1>
          {activeView === VIEW.RUN_DATA && (
            <p className={classes.subtitle}>
              Click a batch ID to view relanding results and download reports
            </p>
          )}
        </div>
      </header>

      <div className={classes.content}>
        {activeView === VIEW.RUN_DATA ? renderRunDataSection() : renderItemWorkflowSection()}
      </div>
    </section>
  );
};

export default LandedCostResults;
